import { cs } from '../../lib/currency';
import { useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Checkbox } from '../ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { DollarSign, Loader2 } from 'lucide-react';
import api from '../../services/api';
import { toast } from 'sonner';

/**
 * Reusable refund dialog used by every admin page that shows a paid record
 * (lane bookings, tournament registrations, enrollments, camps, coaching).
 *
 * Props:
 *   open, onOpenChange                — dialog control
 *   entityType                        — "lane_booking" | "tournament_registration" | "enrollment" | "camp_registration" | "coaching_session"
 *   entity                            — the paid record. Only needs:
 *                                         id, price/amount, refunded_amount,
 *                                         customer_name, payment_method,
 *                                         plus any extra fields used for the
 *                                         summary block (dateLabel, slotLabel).
 *   priceField                        — field name on `entity` holding price.
 *                                       Defaults to "price". For tournaments
 *                                       pass "payment_amount", enrollments
 *                                       "total_amount", etc.
 *   summaryLines                      — [{label, value}] rendered inside the
 *                                       grey summary box so callers control
 *                                       exactly what the admin sees.
 *   allowCancelCheckbox               — show "also cancel this booking" checkbox.
 *   onRefunded                        — callback fired after success.
 */
export default function RefundDialog({
  open,
  onOpenChange,
  entityType,
  entity,
  priceField = 'price',
  summaryLines = [],
  allowCancelCheckbox = true,
  onRefunded,
}) {
  // iter299: walk the same field priority the backend
  // `_resolve_refundable_price` uses so the dialog never lands on
  // `remaining=0` just because the caller passed the wrong `priceField`
  // (e.g. camp registrations have both `payment_amount` and `amount`,
  // and certain legacy rows only populate one of them).
  //
  // iter305: include the entity's stored `surcharge_amount` (CC fee
  // the customer was actually debited) so the "Amount paid" + "Max"
  // refundable lines reflect what was charged. Backend
  // `_resolve_refundable_price` mirrors this logic, so the server
  // accepts the higher amount without rejecting it as "exceeds
  // remaining refundable".
  const price = useMemo(() => {
    const candidates = [
      priceField,
      'payment_amount',
      'amount',
      'total_amount',
      'fee',
      'price',
    ];
    let base = 0;
    for (const f of candidates) {
      const v = Number(entity?.[f]);
      if (Number.isFinite(v) && v > 0) { base = v; break; }
    }
    if (base <= 0) return 0;
    const surcharge = Number(entity?.surcharge_amount);
    if (Number.isFinite(surcharge) && surcharge > 0) {
      return +(base + surcharge).toFixed(2);
    }
    return base;
  }, [entity, priceField]);
  const alreadyRefunded = Number(entity?.refunded_amount || 0);
  const remaining = useMemo(
    () => Math.max(0, +(price - alreadyRefunded).toFixed(2)),
    [price, alreadyRefunded],
  );

  const [amount, setAmount] = useState('');
  const [method, setMethod] = useState('stripe');
  const [reason, setReason] = useState('');
  const [alsoCancel, setAlsoCancel] = useState(false);
  const [saving, setSaving] = useState(false);
  // iter334cc — CC-surcharge auto-refund toggle. Default ON per user
  // policy: whenever a card/link payment had a 3% surcharge stamped,
  // the proportional surcharge on the refunded portion is added to
  // the Stripe refund automatically. Admin can un-check to refund
  // just the base.
  const [includeCcSurcharge, setIncludeCcSurcharge] = useState(true);
  const [breakdown, setBreakdown] = useState(null);
  const [breakdownLoading, setBreakdownLoading] = useState(false);
  // iter369 — split-refund (refund by method / mix).
  const [byMethod, setByMethod] = useState(null);
  const [splitMode, setSplitMode] = useState(false);
  const [splitAmts, setSplitAmts] = useState({});

  useEffect(() => {
    if (!open || !entity) return;
    let cancelled = false;
    api.get('/admin/refunds/by-method', {
      params: {
        entity_type: entityType,
        entity_id: entity.id,
        monthly_invoice_id: entity.monthly_invoice_id || undefined,
      },
    })
      .then((r) => {
        if (cancelled) return;
        const bm = r.data || null;
        setByMethod(bm);
        // Auto-enable split when the invoice was genuinely paid by more
        // than one method (a real-money rail AND account credit).
        const realPaid = (bm?.methods || []).some((m) => m.real_money && m.paid > 0.005);
        const creditPaid = (bm?.methods || []).some((m) => m.method === 'account_credit' && m.paid > 0.005);
        setSplitMode(!!(realPaid && creditPaid));
        const seed = {};
        (bm?.methods || []).forEach((m) => { seed[m.method] = ''; });
        setSplitAmts(seed);
      })
      .catch(() => { if (!cancelled) setByMethod(null); });
    return () => { cancelled = true; };
  }, [open, entity, entityType]);

  const splitTotal = useMemo(
    () => Object.values(splitAmts).reduce((s, v) => s + (parseFloat(v) || 0), 0),
    [splitAmts],
  );
  const splitRemaining = Number(byMethod?.total_refundable || 0);
  // Real-money BASE currently entered (across card/ach/cash/check rails).
  const realEntered = useMemo(
    () => (byMethod?.methods || [])
      .filter((m) => m.real_money)
      .reduce((s, m) => s + (parseFloat(splitAmts[m.method]) || 0), 0),
    [byMethod, splitAmts],
  );
  // Account credit is the flexible absorber: it may take the rest of the
  // invoice BASE that the real-money rails didn't, never pushing the
  // combined refund over the invoice. This stops the admin from inflating
  // the total by raising the credit amount.
  const creditCap = Math.max(0, Math.round((splitRemaining - realEntered) * 100) / 100);
  // Surcharge is a card-only add-on applied PROPORTIONALLY on top of the
  // card BASE being refunded. Base amounts drive the caps; surcharge is
  // previewed/added separately so we never refund more base than billed.
  const splitSurcharge = useMemo(
    () => (byMethod?.methods || [])
      .filter((m) => m.real_money)
      .reduce((s, m) => {
        const v = parseFloat(splitAmts[m.method]) || 0;
        return s + Math.round(v * (Number(m.surcharge_rate) || 0) * 100) / 100;
      }, 0),
    [byMethod, splitAmts],
  );
  // Label of the rail that carries a surcharge (card). Never hard-code
  // "card": ACH/cash/check carry no surcharge so this stays hidden for them.
  const surchargeLabel = (byMethod?.methods || [])
    .find((m) => m.real_money && Number(m.surcharge_rate) > 0)?.label || 'card';
  const splitGrandTotal = Math.round((splitTotal + splitSurcharge) * 100) / 100;
  const capFor = (m) => (m.method === 'account_credit' ? creditCap : Number(m.refundable));
  const splitValid = splitTotal > 0.005 && splitTotal <= splitRemaining + 0.005 &&
    (byMethod?.methods || []).every((m) => {
      const v = parseFloat(splitAmts[m.method]) || 0;
      return v <= capFor(m) + 0.005;
    });

  // Whenever the real-money amount grows, clamp account credit down so the
  // combined refund never exceeds the invoice (admin can't inflate total).
  useEffect(() => {
    if (!splitMode || !byMethod) return;
    const cur = parseFloat(splitAmts.account_credit);
    if (Number.isFinite(cur) && cur > creditCap + 0.005) {
      setSplitAmts((p) => ({ ...p, account_credit: creditCap.toFixed(2) }));
    }
  }, [creditCap, splitMode, byMethod, splitAmts.account_credit]);

  const setSplitAmt = (method, val, cap) => {
    if (val === '' || val === '.') { setSplitAmts((p) => ({ ...p, [method]: val })); return; }
    const n = parseFloat(val);
    if (Number.isFinite(n) && n > cap) { setSplitAmts((p) => ({ ...p, [method]: cap.toFixed(2) })); return; }
    setSplitAmts((p) => ({ ...p, [method]: val }));
  };

  const refundAllAsCredit = () => {
    const seed = {};
    (byMethod?.methods || []).forEach((m) => {
      seed[m.method] = m.method === 'account_credit' ? splitRemaining.toFixed(2) : '0';
    });
    setSplitAmts(seed);
  };

  // Match the ORIGINAL split: max out each real-money rail to what it
  // paid, then fill account credit with the remaining BASE (which equals
  // the credit originally applied when the invoice was fully paid).
  const matchOriginalSplit = () => {
    const seed = {};
    const realSum = (byMethod?.methods || [])
      .filter((m) => m.real_money)
      .reduce((s, m) => s + Number(m.refundable || 0), 0);
    (byMethod?.methods || []).forEach((m) => {
      if (m.real_money) {
        seed[m.method] = Number(m.refundable).toFixed(2);
      } else {
        seed[m.method] = Math.max(0, Math.round((splitRemaining - realSum) * 100) / 100).toFixed(2);
      }
    });
    setSplitAmts(seed);
  };

  const handleSplitSubmit = async () => {
    if (!entity || !splitValid) { toast.error('Enter valid refund amounts'); return; }
    setSaving(true);
    try {
      const splits = (byMethod?.methods || [])
        .map((m) => ({ method: m.method, amount: parseFloat(splitAmts[m.method]) || 0 }))
        .filter((s) => s.amount > 0.005);
      const res = await api.post('/admin/refunds/split', {
        entity_type: entityType,
        entity_id: entity.id,
        monthly_invoice_id: entity.monthly_invoice_id || undefined,
        reason: reason || undefined,
        also_cancel: alsoCancel,
        include_cc_surcharge: includeCcSurcharge,
        splits,
      });
      toast.success(`Refunded ${cs()}${(res.data?.total_refunded || splitTotal).toFixed(2)} across ${splits.length} method(s)`);
      onOpenChange?.(false);
      onRefunded?.(res.data || {});
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Split refund failed');
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    if (!open || !entity) return;
    setAmount(remaining.toFixed(2));
    setMethod(entity?.payment_method === 'online' || entity?.payment_method === 'card' ? 'stripe' : 'cash');
    setReason('');
    setAlsoCancel(false);
    setIncludeCcSurcharge(true);
    setBreakdown(null);
  }, [open, entity, remaining]);

  // iter334cc — Live breakdown preview: whenever amount / method /
  // toggle change and the target is a Stripe refund, ask the backend
  // for the exact split (base + CC surcharge + total). Debounce so
  // rapid keystrokes don't spam the endpoint.
  useEffect(() => {
    if (!open || !entity) return;
    if (method !== 'stripe') {
      setBreakdown(null);
      return;
    }
    const n = parseFloat(amount);
    if (!Number.isFinite(n) || n <= 0) {
      setBreakdown(null);
      return;
    }
    let cancelled = false;
    const handle = setTimeout(() => {
      setBreakdownLoading(true);
      api.post('/admin/refunds/preview', {
        entity_type: entityType,
        entity_id: entity.id,
        amount: n,
        method: 'stripe',
        monthly_invoice_id: entity.monthly_invoice_id || undefined,
        include_cc_surcharge: includeCcSurcharge,
      })
        .then((r) => { if (!cancelled) setBreakdown(r.data); })
        .catch(() => { if (!cancelled) setBreakdown(null); })
        .finally(() => { if (!cancelled) setBreakdownLoading(false); });
    }, 250);
    return () => { cancelled = true; clearTimeout(handle); };
  }, [open, entity, entityType, amount, method, includeCcSurcharge]);

  const parsedAmount = parseFloat(amount);
  const isValid = Number.isFinite(parsedAmount) && parsedAmount > 0 && parsedAmount <= remaining + 0.001;

  const handleAmountChange = (val) => {
    // Accept any keystroke but clamp commits above `remaining` to the exact
    // remaining value. Avoids "$30.00 exceeds $29.99" style errors when the
    // admin rounds up by a penny.
    if (val === '' || val === '.') {
      setAmount(val);
      return;
    }
    const n = parseFloat(val);
    if (Number.isFinite(n) && n > remaining) {
      setAmount(remaining.toFixed(2));
      return;
    }
    setAmount(val);
  };

  const handleSubmit = async () => {
    if (!entity || !isValid) {
      toast.error('Enter a valid refund amount');
      return;
    }
    setSaving(true);
    try {
      const res = await api.post('/admin/refunds', {
        entity_type: entityType,
        entity_id: entity.id,
        amount: parsedAmount,
        method,
        reason: reason || undefined,
        also_cancel: alsoCancel,
        // iter307ds — When the caller knows the specific monthly
        // invoice being refunded (Parent Invoices panel does), pass
        // its id explicitly so the backend uses the MI's cycle-specific
        // Stripe PI instead of grabbing an unrelated PT from a prior
        // cycle. Harmless when omitted / auto-detected server-side.
        monthly_invoice_id: entity.monthly_invoice_id || undefined,
        // iter334cc — include CC surcharge on Stripe refunds when the
        // toggle is on. Backend ignores for offline methods.
        include_cc_surcharge: method === 'stripe' ? includeCcSurcharge : false,
      });
      const d = res.data || {};
      // iter306p: humanise toast for all 6 supported methods.
      const METHOD_LABEL = {
        stripe: 'Stripe refund issued',
        credit: 'Account credit added',
        cash: 'Cash refund recorded',
        check: 'Check refund recorded',
        zelle: 'Zelle refund recorded',
        venmo: 'Venmo refund recorded',
      };
      const methodLabel = METHOD_LABEL[method] || 'Refund recorded';
      toast.success(
        `${methodLabel} · ${cs()}${parsedAmount.toFixed(2)}${
          d.fully_refunded ? ' (fully refunded)' : ''
        }${alsoCancel ? ' · record cancelled' : ''}`,
      );
      onOpenChange?.(false);
      onRefunded?.(d);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Refund failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="refund-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <DollarSign className="h-5 w-5 text-amber-600" />
            Issue refund
          </DialogTitle>
          <DialogDescription>
            Refund a custom amount back to the customer. Partial refunds are supported —
            you can issue multiple until the full amount is refunded.
          </DialogDescription>
        </DialogHeader>

        {entity && (
          <div className="text-xs bg-slate-50 border rounded-md p-3 space-y-0.5">
            {summaryLines.map((line) => (
              <div key={line.label}>
                <strong>{line.label}:</strong> {line.value}
              </div>
            ))}
            <div>
              <strong>Amount paid:</strong> {cs()}{Number(byMethod?.total_paid ?? price).toFixed(2)}
              {(() => {
                const refunded = Number(byMethod?.total_refunded ?? alreadyRefunded ?? 0);
                if (refunded <= 0.005) return null;
                const net = Math.max(0, Math.round((Number(byMethod?.total_paid ?? price) - refunded) * 100) / 100);
                return (
                  <span className="text-slate-500" data-testid="refund-already-refunded">
                    {' '}· refunded {cs()}{refunded.toFixed(2)} · net paid {cs()}{net.toFixed(2)}
                  </span>
                );
              })()}
            </div>
          </div>
        )}

        {byMethod && (byMethod.methods || []).length > 0 && (
          <label className="flex items-center gap-2 text-sm cursor-pointer pt-1" data-testid="refund-split-toggle-label">
            <Checkbox
              checked={splitMode}
              onCheckedChange={(v) => setSplitMode(!!v)}
              data-testid="refund-split-toggle"
            />
            <span>Split across payment methods</span>
          </label>
        )}

        {splitMode && byMethod ? (
          <div className="space-y-4 pt-2" data-testid="refund-split-section">
            <div className="flex gap-2">
              <Button type="button" variant="outline" size="sm" className="text-xs h-7"
                onClick={matchOriginalSplit} data-testid="refund-split-match-btn">
                Match original split
              </Button>
              <Button type="button" variant="outline" size="sm" className="text-xs h-7"
                onClick={refundAllAsCredit} data-testid="refund-split-all-credit-btn">
                Refund all as account credit
              </Button>
            </div>
            {(byMethod.methods || []).map((m) => {
              const cap = capFor(m);
              const v = parseFloat(splitAmts[m.method]) || 0;
              const rowSur = m.real_money
                ? Math.round(v * (Number(m.surcharge_rate) || 0) * 100) / 100
                : 0;
              return (
              <div key={m.method} className="space-y-1.5" data-testid={`refund-split-row-${m.method}`}>
                <Label className="block text-sm font-medium">
                  {m.label}
                  {m.real_money
                    ? <span className="text-[11px] font-normal text-slate-500 ml-1">base paid {cs()}{Number(m.paid).toFixed(2)} · refundable {cs()}{Number(m.refundable).toFixed(2)}</span>
                    : <span className="text-[11px] font-normal text-slate-500 ml-1">store credit · up to {cs()}{creditCap.toFixed(2)} (no surcharge)</span>}
                </Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500">$</span>
                  <Input
                    type="number" step="0.01" min="0"
                    max={cap.toFixed(2)}
                    value={splitAmts[m.method] ?? ''}
                    onChange={(e) => setSplitAmt(m.method, e.target.value, cap)}
                    className="pl-7"
                    data-testid={`refund-split-input-${m.method}`}
                  />
                </div>
                {m.real_money && Number(m.surcharge_rate) > 0 && v > 0.005 && (
                  <p className="text-[11px] text-slate-500" data-testid={`refund-split-surcharge-${m.method}`}>
                    + {cs()}{rowSur.toFixed(2)} CC surcharge → <strong>{cs()}{(v + rowSur).toFixed(2)}</strong> back to {m.label.toLowerCase()}
                  </p>
                )}
              </div>
              );
            })}
            <div className="border-t pt-2 space-y-1 tabular-nums" data-testid="refund-split-total">
              <div className="text-xs flex justify-between">
                <span className="text-slate-600">Base refund</span>
                <span className={splitTotal > splitRemaining + 0.005 ? 'text-rose-600 font-semibold' : 'font-medium'}>
                  {cs()}{splitTotal.toFixed(2)} / {cs()}{splitRemaining.toFixed(2)}
                </span>
              </div>
              {splitSurcharge > 0.005 && (
                <div className="text-xs flex justify-between text-slate-500">
                  <span>{surchargeLabel} surcharge</span>
                  <span>+{cs()}{splitSurcharge.toFixed(2)}</span>
                </div>
              )}
              <div className="text-sm flex justify-between font-semibold pt-0.5">
                <span>Total to refund</span>
                <span className={splitTotal > splitRemaining + 0.005 ? 'text-rose-600' : ''}>
                  {cs()}{splitGrandTotal.toFixed(2)}
                </span>
              </div>
            </div>
            <div className="pt-1">
              <Label htmlFor="refund-reason-split" className="block mb-1">Reason (optional)</Label>
              <Input id="refund-reason-split" placeholder="e.g. Customer complaint / double charge"
                value={reason} onChange={(e) => setReason(e.target.value)} maxLength={300}
                data-testid="refund-reason-split-input" />
            </div>
            {allowCancelCheckbox && (
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <Checkbox checked={alsoCancel} onCheckedChange={(v) => setAlsoCancel(!!v)} data-testid="refund-split-cancel-checkbox" />
                <span>Also cancel this record</span>
              </label>
            )}
          </div>
        ) : (
        <div className="space-y-3 pt-2">
          <div>
            <Label htmlFor="refund-amount">Refund amount (USD)</Label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500">$</span>
              <Input
                id="refund-amount"
                type="number"
                step="0.01"
                min="0.01"
                max={remaining.toFixed(2)}
                value={amount}
                onChange={(e) => handleAmountChange(e.target.value)}
                className="pl-7 pr-16"
                data-testid="refund-amount-input"
              />
              <button
                type="button"
                onClick={() => setAmount(remaining.toFixed(2))}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-xs font-medium text-amber-700 hover:text-amber-800 hover:underline"
                data-testid="refund-amount-max-btn"
              >
                Max
              </button>
            </div>
            <p className="text-[11px] text-slate-500 mt-1">
              Remaining refundable: {cs()}{remaining.toFixed(2)}
            </p>
          </div>

          <div>
            <Label htmlFor="refund-method">Refund method</Label>
            <Select value={method} onValueChange={setMethod}>
              <SelectTrigger id="refund-method" data-testid="refund-method-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="stripe" data-testid="refund-method-stripe">Stripe refund (original card/bank)</SelectItem>
                <SelectItem value="credit" data-testid="refund-method-credit">Account credit (applies to future payments)</SelectItem>
                <SelectItem value="cash" data-testid="refund-method-cash">Cash (record only, no money moved)</SelectItem>
                <SelectItem value="check" data-testid="refund-method-check">Check (record only)</SelectItem>
                <SelectItem value="zelle" data-testid="refund-method-zelle">Zelle (record only)</SelectItem>
                <SelectItem value="venmo" data-testid="refund-method-venmo">Venmo (record only)</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* iter334cc — CC surcharge toggle + live breakdown */}
          {method === 'stripe' && breakdown?.eligible_for_surcharge && (
            <div
              className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 space-y-2"
              data-testid="refund-surcharge-block"
            >
              <label className="flex items-start gap-2 text-sm cursor-pointer">
                <Checkbox
                  checked={includeCcSurcharge}
                  onCheckedChange={(v) => setIncludeCcSurcharge(!!v)}
                  data-testid="refund-cc-surcharge-toggle"
                  className="mt-0.5"
                />
                <span className="text-amber-900">
                  Also refund the pro-rata 3% CC processing fee
                  <span className="block text-[11px] text-amber-800 mt-0.5">
                    The original payment carried a {cs()}
                    {Number(breakdown.original_paid_surcharge || 0).toFixed(2)} card
                    surcharge on {cs()}{Number(breakdown.original_paid_base || 0).toFixed(2)}.
                    Refunding proportionally keeps the parent whole.
                  </span>
                </span>
              </label>
              <div className="text-xs text-amber-900 border-t border-amber-200 pt-1.5 space-y-0.5 tabular-nums">
                <div className="flex justify-between">
                  <span>Base refund</span>
                  <span data-testid="refund-breakdown-base">
                    {cs()}{Number(breakdown.base_refund || 0).toFixed(2)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span>
                    CC surcharge refund
                    {breakdown.surcharge_rate > 0 && (
                      <span className="text-[10px] text-amber-700 ml-1">
                        ({(breakdown.surcharge_rate * 100).toFixed(2)}%)
                      </span>
                    )}
                  </span>
                  <span data-testid="refund-breakdown-surcharge">
                    {includeCcSurcharge
                      ? `${cs()}${Number(breakdown.surcharge_refund || 0).toFixed(2)}`
                      : '—'}
                  </span>
                </div>
                <div className="flex justify-between font-semibold border-t border-amber-200 pt-1">
                  <span>Total refunded to card</span>
                  <span data-testid="refund-breakdown-total">
                    {cs()}{Number(
                      includeCcSurcharge
                        ? breakdown.total_refund
                        : breakdown.base_refund,
                    ).toFixed(2)}
                  </span>
                </div>
              </div>
            </div>
          )}
          {method === 'stripe' && breakdownLoading && !breakdown && (
            <p className="text-[11px] text-slate-500">Calculating breakdown…</p>
          )}

          <div>
            <Label htmlFor="refund-reason">Reason (optional)</Label>
            <Input
              id="refund-reason"
              placeholder="e.g. Customer complaint / double charge"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              maxLength={300}
              data-testid="refund-reason-input"
            />
          </div>

          {allowCancelCheckbox && (
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <Checkbox
                checked={alsoCancel}
                onCheckedChange={(v) => setAlsoCancel(!!v)}
                data-testid="refund-cancel-checkbox"
              />
              <span>Also cancel this record</span>
            </label>
          )}
        </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange?.(false)} disabled={saving}>
            Cancel
          </Button>
          <Button
            onClick={splitMode ? handleSplitSubmit : handleSubmit}
            disabled={saving || (splitMode ? !splitValid : !isValid)}
            className="bg-amber-600 hover:bg-amber-700 text-white"
            data-testid="confirm-refund-btn"
          >
            {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            {saving
              ? 'Processing…'
              : splitMode
                ? `Refund ${cs()}${splitGrandTotal.toFixed(2)}`
                : `Refund ${cs()}${Number(
                  method === 'stripe' && includeCcSurcharge && breakdown?.eligible_for_surcharge
                    ? breakdown.total_refund
                    : parsedAmount || 0,
                ).toFixed(2)}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
